import { ChangeDetectionStrategy, Component, computed, input, OnInit, output, signal } from '@angular/core';

import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { RessourcesModule } from '@ressources/client/ressources.module';

export interface ToggleOption<T> {
    isDisable?: boolean;
    title: string;
    tooltip?: string;
    value: T;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, RessourcesModule],
    selector: 'app-text-toggle',
    templateUrl: './text-toggle.component.html',
})
export class TextToggleComponent<T> implements OnInit {
    public readonly allowMultipleSelection = input<boolean>(false);
    public readonly defaultValue = input<T[]>([]);
    public readonly options = input.required<ToggleOption<T>[]>();
    public readonly selectionChange = output<T[]>();

    protected readonly ButtonType = ButtonType;
    protected readonly selectedValues = signal<T[]>([]);
    protected readonly selectedValueSet = computed<Set<T>>(() => {
        return new Set(this.selectedValues());
    });

    public ngOnInit(): void {
        this.selectedValues.set(this.defaultValue().length ? this.defaultValue() : this.getInitialSelection());
    }

    protected toggleOption(option: ToggleOption<T>): void {
        if (option.isDisable || this.selectedValueSet().has(option.value)) {
            return;
        }

        if (this.allowMultipleSelection()) {
            this.toggleMultiple(option);

            return;
        }

        this.selectedValues.set([option.value]);
        this.selectionChange.emit(this.selectedValues());
    }

    private getInitialSelection(): T[] {
        const firstEnabledOption: ToggleOption<T> | undefined = this.options().find(
            (option: ToggleOption<T>): boolean => !option.isDisable,
        );

        return firstEnabledOption ? [firstEnabledOption.value] : [];
    }

    private toggleMultiple(option: ToggleOption<T>): void {
        const currentSelection: T[] = this.selectedValues();
        const isAlreadySelected: boolean = this.selectedValueSet().has(option.value);

        if (isAlreadySelected) {
            if (currentSelection.length === 1) {
                return;
            }

            this.selectedValues.set(currentSelection.filter((value: T): boolean => value !== option.value));
        } else {
            this.selectedValues.set([...currentSelection, option.value]);
        }

        this.selectionChange.emit(this.selectedValues());
    }
}
